import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useIdleContext } from "../context/IdleContext";
import { switchApiUrl } from "../pages/const";
import "./auth-fields.css";

function AccessPINOverlay({
  onSuccess,
  onCancel,
  show = false,
  /** When false, ignore idle/lock context for open state (e.g. login PIN step). */
  listenToIdle = true,
}) {
  const [pin, setPin] = useState(["", "", "", ""]);
  const [isValid, setIsValid] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const inputRefs = useRef([]);
  const focusTimerRef = useRef(null);
  const navigate = useNavigate();
  const { showIdleModal, hideIdleModal } = useIdleContext();

  const accessPin = process.env.REACT_APP_ACCESS_PIN;
  const quotePin = process.env.REACT_APP_QUOTE_PIN;
  const dihranPin = process.env.REACT_APP_DIHRAN_PIN;

  const isModalOpen = show || (listenToIdle && showIdleModal);

  const focusFirstInput = useCallback(() => {
    const el = inputRefs.current[0];
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
      if (typeof el.select === "function") el.select();
    } catch {
      el.focus();
    }
  }, []);

  // Reset + focus when modal opens (single effect — no duplicates)
  useEffect(() => {
    if (!isModalOpen) return;

    setPin(["", "", "", ""]);
    setIsValid(null);
    setIsComplete(false);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus after paint; retry briefly in case the input mounts late
    const rafId = requestAnimationFrame(() => {
      focusFirstInput();
      focusTimerRef.current = window.setTimeout(focusFirstInput, 80);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current);
        focusTimerRef.current = null;
      }
      document.body.style.overflow = prevOverflow;
    };
  }, [isModalOpen, focusFirstInput]);

  // Prevent back navigation while locked
  useEffect(() => {
    if (!isModalOpen) return;

    const handlePopState = (e) => {
      e.preventDefault();
      window.history.pushState(null, null, window.location.href);
      return false;
    };

    window.history.pushState(null, null, window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;

    const pinString = pin.join("");
    setIsComplete(pinString.length === 4);

    if (pinString.length === 4) {
      const isAccessPin = pinString === accessPin;
      const isQuotePin = pinString === quotePin;
      const isDihranPin = Boolean(dihranPin) && pinString === dihranPin;
      const isCorrect = isAccessPin || isQuotePin || isDihranPin;
      const pinType = isDihranPin
        ? "dihran"
        : isQuotePin
          ? "quote"
          : "access";

      setIsValid(isCorrect);

      if (isCorrect) {
        if (isQuotePin) {
          switchApiUrl("quote");
        } else {
          switchApiUrl("access");
        }

        const successTimer = window.setTimeout(() => {
          // Read saved path before hideIdleModal clears it from localStorage.
          const returnPath = localStorage.getItem("accessPinReturnPath");

          // Always clear lock state so protected UI can remount after unlock.
          hideIdleModal();

          if (onSuccess) {
            onSuccess({ pinType });
          }

          window.setTimeout(() => {

            // Determine which module prefix the saved path belongs to.
            const savedIsGirvi =
              returnPath &&
              (returnPath.startsWith("/girvi") ||
                returnPath.startsWith("/addgirvi") ||
                returnPath.startsWith("/editgirvi") ||
                returnPath.startsWith("/reversegirvi") ||
                returnPath.startsWith("/addreversegirvi") ||
                returnPath.startsWith("/editreversegirvi"));
            const savedIsQuote =
              returnPath && returnPath.startsWith("/quote");
            const savedIsAccess =
              returnPath && !savedIsGirvi && !savedIsQuote;

            // Resume only when the entered PIN belongs to the SAME module
            // as the page that was locked. Entering a different-module PIN
            // always redirects to that module's dashboard.
            const shouldResume =
              returnPath &&
              returnPath !== "/" &&
              ((pinType === "access" && savedIsAccess) ||
                (pinType === "dihran" && savedIsGirvi) ||
                (pinType === "quote" && savedIsQuote));

            if (shouldResume) {
              const currentPath = window.location.pathname;
              if (currentPath !== returnPath) {
                navigate(returnPath);
              }
              // Already on the right page — just let the overlay unmount.
            } else {
              // Different module PIN or no saved path → go to module dashboard.
              const targetPath =
                pinType === "dihran"
                  ? "/girvi"
                  : pinType === "quote"
                    ? "/quote/dashboard"
                    : "/dashboard";

              const currentPath = window.location.pathname;
              if (currentPath === targetPath) {
                navigate(targetPath, {
                  replace: true,
                  state: { forceRefresh: true, timestamp: Date.now() },
                });
              } else {
                navigate(targetPath);
              }
            }
          }, 50);
        }, 100);

        return () => clearTimeout(successTimer);
      }

      const errorTimer = window.setTimeout(() => {
        setPin(["", "", "", ""]);
        setIsValid(null);
        setIsComplete(false);
        focusFirstInput();
      }, 1500);

      return () => clearTimeout(errorTimer);
    }

    setIsValid(null);
    return undefined;
  }, [
    pin,
    isModalOpen,
    accessPin,
    quotePin,
    dihranPin,
    listenToIdle,
    showIdleModal,
    hideIdleModal,
    onSuccess,
    navigate,
    focusFirstInput,
  ]);

  const handleInputChange = (index, value) => {
    if (value.length > 1) return;
    if (value !== "" && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    if (value !== "" && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (pin[index] === "" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newPin = [...pin];
        newPin[index] = "";
        setPin(newPin);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    if (/^\d{4}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setPin(digits);
      inputRefs.current[3]?.focus();
    }
  };

  const handleOverlayMouseDown = (e) => {
    // Keep focus in PIN fields: clicking empty overlay area refocuses first box
    if (e.target === e.currentTarget) {
      e.preventDefault();
      focusFirstInput();
    }
  };

  const getStatusMessage = () => {
    if (isComplete) {
      if (isValid === true) {
        return (
          <div className="alert alert-success py-2 mb-0" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            PIN verified successfully!
          </div>
        );
      }
      if (isValid === false) {
        return (
          <div className="alert alert-danger py-2 mb-0" role="alert">
            <i className="fas fa-times-circle me-2"></i>
            Invalid PIN. Please try again.
          </div>
        );
      }
    }
    return null;
  };

  if (!isModalOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Enter Access PIN"
      onMouseDown={handleOverlayMouseDown}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "white",
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="auth-fields-scope"
        style={{ maxWidth: "400px", width: "100%", padding: "20px" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-4">
          <div className="mb-3">
            <i
              className="fas fa-lock text-warning"
              style={{ fontSize: "4rem" }}
            ></i>
          </div>
          <h3 className="fw-bold text-dark mb-2">Enter Access PIN</h3>
        </div>

        <div className="row g-2 mb-4" onPaste={handlePaste}>
          {pin.map((digit, index) => (
            <div key={index} className="col-3">
              <input
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                autoFocus={index === 0}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onFocus={(e) => e.target.select()}
                className="form-control text-center fw-bold fs-4 access-pin-input"
                style={{
                  height: "60px",
                  fontSize: "24px",
                }}
              />
            </div>
          ))}
        </div>

        <div className="mb-4" style={{ minHeight: "48px" }}>
          {getStatusMessage()}
        </div>
      </div>
    </div>
  );
}

export default AccessPINOverlay;
